import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import SlideOutSidebar from 'ui-components/slide-out-sidebar/slideOutSidebar';
import Loading from 'ui-components/loading/loading';
import {
    ReviewDispatchLaneCount,
    ReviewDispatchResponse,
} from 'app/data/static-load/model';
import { staticLoadSelector } from 'slices/static-load/staticLoadSlice';
import { useReview, useReviewDispatch } from '../hooks/useReviewDispatch';
import { useSummaryTiles } from '../hooks/useSummaryTiles';
import { formatDispatchCount, formatExternalCost } from '../components/weekSummaryTiles';
import '../cloneWeek.scss';
import Table from 'ui-components/table/table';
import { getReviewDispatch } from './getReviewDispatch';
import Button, { ButtonSizes, ButtonThemes } from 'ui-components/button/button';
import { useBuildWeek } from '../hooks/useBuildWeek';

const formatCount = (value?: number): string => formatDispatchCount(value);

const formatCost = (value?: number | null): string => formatExternalCost(value);

const LANE_COUNT_BY_DAY: { label: string; key: keyof ReviewDispatchLaneCount }[] = [
    { label: 'Monday', key: 'monday' },
    { label: 'Tuesday', key: 'tuesday' },
    { label: 'Wednesday', key: 'wednesday' },
    { label: 'Thursday', key: 'thursday' },
    { label: 'Friday', key: 'friday' },
    { label: 'Saturday', key: 'saturday' },
    { label: 'Sunday', key: 'sunday' },
];

const formatWeekLabel = (weekStartDate?: string): string => {
    if (!weekStartDate) return '—';
    const start = moment(weekStartDate);
    const end = moment(weekStartDate).add(6, 'days');
    return `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}`;
};

const getDaysActiveLabel = (laneCount?: ReviewDispatchLaneCount): string => {
    if (!laneCount) return '—';
    const activeDays = LANE_COUNT_BY_DAY.filter(({ key }) => (laneCount[key] ?? 0) > 0).length;
    return activeDays > 0 ? `${activeDays} days` : '—';
};

const buildDispatchesPerDay = (laneCount?: ReviewDispatchLaneCount) =>
    LANE_COUNT_BY_DAY.map(({ label, key }) => ({
        date: label,
        laneCount: laneCount?.[key] ?? 0,
    }));

const buildCarrierTableData = (reviewData?: ReviewDispatchResponse) => {
    const carriers = reviewData?.carrierSummary?.carriers || [];
    const totalDrops = reviewData?.carrierSummary?.totalDrops ?? 0;
    const totalLanes = reviewData?.laneCount?.totalLanes ?? 0;

    return [
        ...carriers,
        { carrierName: 'Total', dropCount: totalDrops, laneCount: totalLanes },
    ];
};

interface ReviewDispatchSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    onDispatchSuccess?: () => void;
}

const ReviewDispatchSidebar: React.FC<ReviewDispatchSidebarProps> = ({
    isOpen,
    onClose,
    onDispatchSuccess,
}) => {
    const { selectedDivision, weekStartDate } = useSelector(staticLoadSelector);

    const { data: reviewData, isLoading: isReviewLoading } = useReview(
        selectedDivision,
        weekStartDate,
        isOpen
    );

    const { data: summaryTiles, isLoading: isSummaryLoading } = useSummaryTiles(
        selectedDivision,
        weekStartDate,
        isOpen
    );

    const isLoading = isReviewLoading || isSummaryLoading;

    const reviewDispatchMutation = useReviewDispatch();
    const addDispatchMutation = useBuildWeek(() => {
        onDispatchSuccess?.();
        onClose();
    });

    const carrierTableData = useMemo(
        () => buildCarrierTableData(reviewData),
        [reviewData]
    );

    const dispatchesPerDay = useMemo(
        () => buildDispatchesPerDay(reviewData?.laneCount),
        [reviewData?.laneCount]
    );

    const totalLanesForTitle = reviewData?.laneCount?.totalLanes ?? 0;
    const sourceWeekLabel = formatWeekLabel(reviewData?.weekSummary?.sourceWeekStartDate);
    const daysActive = getDaysActiveLabel(reviewData?.laneCount);

    const handleDispatchWeek =  () => {
        reviewDispatchMutation.mutate({
            division: selectedDivision,
            weekStartDate: weekStartDate,
        },{
            onSuccess: () => {
                addDispatchMutation.mutate({
                    weekStartDate: weekStartDate,
                    division: selectedDivision,
                });
            },
        });
    };

    return (
        <SlideOutSidebar
            show={isOpen}
            onClose={onClose}
            header="Review & Dispatch"
            size="medium-860"
            className="filter-sidebar"
        >
            {isLoading ? (
                <Loading isLoading={true} />
            ) : (
                <div className='review-dispatch-sidebar-container'>
                    <div className='review-dispatch-sidebar-container__summary'>
                        <div className='review-dispatch-sidebar-container__summary__item'>
                            <div className='review-dispatch-sidebar-container__label'>Total Dispatches</div>
                            <div className='review-dispatch-sidebar-container__value'>
                                {formatCount(summaryTiles?.totalDispatches)}
                            </div>
                        </div>
                        <div className='review-dispatch-sidebar-container__summary__item'>
                            <div className='review-dispatch-sidebar-container__label'>XGS Dispatches</div>
                            <div className='review-dispatch-sidebar-container__value'>
                                {formatCount(summaryTiles?.xgsDispatches)}
                            </div>
                        </div>
                        <div className='review-dispatch-sidebar-container__summary__item'>
                            <div className='review-dispatch-sidebar-container__label'>External Dispatches</div>
                            <div className='review-dispatch-sidebar-container__value'>
                                {formatCount(summaryTiles?.externalDispatches)}
                            </div>
                        </div>
                        <div className='review-dispatch-sidebar-container__summary__item'>
                            <div className='review-dispatch-sidebar-container__label'>External Cost</div>
                            <div className='review-dispatch-sidebar-container__value'>
                                {formatCost(summaryTiles?.externalCost)}
                            </div>
                        </div>
                    </div>
                    <div className='review-dispatch-sidebar-container__week_details'>
                        <div className='review-dispatch-sidebar-container__details'>
                            <div className='review-dispatch-sidebar-container__details__title'>Week Details</div>
                            <div className='review-dispatch-sidebar-container__details__content'>
                                <div className='review-dispatch-sidebar-container__summary__item'>
                                    <div className='review-dispatch-sidebar-container__label'>Source Week</div>
                                    <div className='review-dispatch-sidebar-container__value'>
                                        {sourceWeekLabel}
                                    </div>
                                </div>
                                <div className='review-dispatch-sidebar-container__summary__item'>
                                    <div className='review-dispatch-sidebar-container__label'>Days Active</div>
                                    <div className='review-dispatch-sidebar-container__value'>
                                        {daysActive}
                                    </div>
                                </div>
                                <div className='review-dispatch-sidebar-container__summary__item'>
                                    <div className='review-dispatch-sidebar-container__label'>Total Lanes</div>
                                    <div className='review-dispatch-sidebar-container__value'>
                                        {reviewData?.laneCount?.totalLanes ?? '—'}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className='review-dispatch-sidebar-container__dispatchesPerDay'>
                            <div className='review-dispatch-sidebar-container__dispatchesPerDay__title'>
                                Dispatches by day ({totalLanesForTitle} lanes)
                            </div>
                            <div className='review-dispatch-sidebar-container__dispatchesPerDay__content'>
                                {dispatchesPerDay.map((day) => (
                                    <div
                                        className='review-dispatch-sidebar-container__dispatchesPerDay__item'
                                        key={day.date}
                                    >
                                        <div className='review-dispatch-sidebar-container__dispatchesPerDay__label'>
                                            {day.date}
                                        </div>
                                        <div className='review-dispatch-sidebar-container__dispatchesPerDay__value'>
                                            {day.laneCount} lanes
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className='review-dispatch-sidebar-container__carrier-summary'>
                        Carrier Summary ({reviewData?.carrierSummary?.carrierCount ?? '—'} Carriers)
                    </div>
                    <Table data={carrierTableData} columns={getReviewDispatch()} />
                    <div className='review-dispatch-sidebar-container__buttons'>
                        <Button
                            type="button"
                            theme={ButtonThemes.gray}
                            size={ButtonSizes.auto}
                            onClick={onClose}
                            disabled={reviewDispatchMutation.isLoading}
                        >
                            Close
                        </Button>
                        <Button
                            type="button"
                            theme={ButtonThemes.blue}
                            size={ButtonSizes.auto}
                            onClick={handleDispatchWeek}
                            spinner={reviewDispatchMutation.isLoading}
                            disabled={reviewDispatchMutation.isLoading || isReviewLoading}
                        >
                            Dispatch Week
                        </Button>
                    </div>
                </div>
            )}
        </SlideOutSidebar>
    );
};

export default ReviewDispatchSidebar;
